import React, { useEffect, useMemo } from "react";
import useTranslator from "../../../hooks/useTranslator";

interface ReviewScreenProps {
  image: File | Blob;
  side: "front" | "back";
  error: string | null;
  onConfirm: () => void;
  onRetake: () => void;
}

const getTranslatedText = (
  _t: (key: string, context?: string) => string,
  key: string,
  fallback: string
) => {
  const translated = _t(key, fallback);
  return translated === key ? fallback : translated;
};

const ReviewScreen: React.FC<ReviewScreenProps> = ({
  image,
  side,
  error,
  onConfirm,
  onRetake,
}) => {
  const { _t } = useTranslator();

  const imageUrl = useMemo(() => URL.createObjectURL(image), [image]);

  useEffect(() => () => URL.revokeObjectURL(imageUrl), [imageUrl]);

  const title =
    side === "front"
      ? getTranslatedText(
          _t,
          "doesFrontLookGood",
          "Does the front look good?"
        )
      : getTranslatedText(_t, "doesBackLookGood", "Does the back look good?");

  return (
    <div className="mobile-capture-review-screen">
      <div className="mobile-capture-review-header">
        <span>{getTranslatedText(_t, "photoReady", "Photo ready")}</span>
        <h3>{title}</h3>
      </div>

      <div className="mobile-capture-review-image">
        <img src={imageUrl} alt={_t("mobileCaptureCapturedImage")} />
      </div>

      <p className="mobile-capture-review-hint">
        {getTranslatedText(
          _t,
          "checkReadableNoReflections",
          "Check that everything is readable and there are no reflections"
        )}
      </p>

      {error && (
        <div className="mobile-capture-review-error">
          <p>{error}</p>
        </div>
      )}

      <div className="mobile-capture-review-actions">
        <button
          type="button"
          className="mobile-capture-btn mobile-capture-btn-secondary"
          onClick={onRetake}
        >
          {getTranslatedText(_t, "retake", "Retake")}
        </button>
        <button
          type="button"
          className="mobile-capture-btn mobile-capture-btn-confirm"
          onClick={onConfirm}
        >
          {getTranslatedText(_t, "usePhoto", "Use photo")}
        </button>
      </div>
    </div>
  );
};

export default ReviewScreen;
